import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CloudOff, Plus, Smartphone } from 'lucide-react';
import { useDeviceVault } from '../../contexts/DeviceVaultContext';
import { assessmentApi, staffApi } from '../../services/assessmentApi';
import { listForms } from '../../services/assessmentStore';
import { pendingCount, syncPendingForms } from '../../services/assessmentSync';
import StartAssessmentDialog from './StartAssessmentDialog';
import { SurveyFollowUpBadge } from './SubmitTools';
import { FORM_TYPE_SHORT, OPEN_STATUSES, STATUS_META, StatusBadge, formatDate } from './statusMeta';

const TABS = [
  { id: 'open', label: 'Open', statuses: OPEN_STATUSES },
  { id: 'assigned', label: 'Assigned', statuses: ['assigned'] },
  { id: 'in_progress', label: 'In progress', statuses: ['in_progress'] },
  { id: 'waiting_on_memo', label: 'Waiting on memo', statuses: ['waiting_on_memo'] },
  { id: 'ready_to_submit', label: 'Ready to submit', statuses: ['ready_to_submit'] },
  { id: 'submitted', label: 'Submitted', statuses: ['submitted'] },
  { id: 'survey', label: 'Survey outstanding', statuses: ['submitted'], survey: 'pending' },
  { id: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
];

export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

// iPhone/iPad Safari may clear a website's stored data after about a week
// without a visit; a site added to the Home Screen is exempt. Offline drafts
// are only as safe as that storage, so iOS users are asked to install.
function InstallHint() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('wmf_install_hint_dismissed') === '1'; } catch { return false; }
  });
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.navigator.standalone === true
    || window.matchMedia?.('(display-mode: standalone)').matches;
  if (!ios || standalone || dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem('wmf_install_hint_dismissed', '1'); } catch { /* ignore */ }
    setDismissed(true);
  };
  return (
    <div className="mb-4 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-900 ring-1 ring-blue-200">
      <Smartphone className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="flex-1">
        <span className="font-medium">Add this portal to your Home Screen.</span> On iPhone and iPad this keeps
        assessments saved offline from being cleared by Safari. Tap the Share button, then &ldquo;Add to Home Screen&rdquo;.
      </p>
      <button type="button" onClick={dismiss} className="text-xs text-blue-700 underline">Dismiss</button>
    </div>
  );
}

// A form started on this device that has not reached the server yet, shaped
// like a server summary so the list can show it alongside the rest.
function localSummary(record) {
  return {
    id: record.uuid,
    formType: record.formType,
    status: 'local',
    studentId: record.studentId,
    studentName: record.studentName,
    therapistId: record.therapistId,
    therapistName: record.therapistName,
    assessmentDate: record.createPayload?.assessmentDate,
    progress: null,
    requiresMemo: record.formType === 'Initial Assessment',
    memoComplete: false,
  };
}

function FormRow({ form, unsynced, showTherapist }) {
  const pct = form.progress?.total ? Math.round((form.progress.answered / form.progress.total) * 100) : null;
  return (
    <li>
      <Link to={`/dashboard/assessments/${form.id}`}
        className="block rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-300 hover:shadow">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-900">{form.studentName}</p>
            <p className="mt-0.5 text-sm text-slate-500">
              {FORM_TYPE_SHORT[form.formType]}
              {form.assessmentDate && <> · {formatDate(form.assessmentDate)}</>}
              {showTherapist && form.therapistName && <> · {form.therapistName}</>}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {form.status === 'submitted' && <SurveyFollowUpBadge survey={form.survey} />}
            {unsynced > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-200">
                <Smartphone className="h-3 w-3" /> Unsynced on this device
              </span>
            )}
            <StatusBadge status={form.status} />
          </div>
        </div>
        {pct !== null && form.status !== 'submitted' && form.status !== 'cancelled' && (
          <div className="mt-3 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs tabular-nums text-slate-500">{form.progress.answered}/{form.progress.total}</span>
            {form.requiresMemo && !form.memoComplete && (
              <span className="text-xs text-amber-700">Memo needed</span>
            )}
          </div>
        )}
      </Link>
    </li>
  );
}

export default function AssessmentsPage() {
  const vault = useDeviceVault();
  const { identity: user, offline: offlineSession } = vault;
  const queryClient = useQueryClient();
  const privileged = user.role === 'admin' || user.role === 'manager';
  const online = useOnline() && !offlineSession;
  const [tab, setTab] = useState('open');
  const [therapistFilter, setTherapistFilter] = useState(privileged ? '' : String(user.userId));
  const [starting, setStarting] = useState(false);
  const [localRecords, setLocalRecords] = useState([]);

  const list = useQuery({
    queryKey: ['assessments', therapistFilter],
    queryFn: () => assessmentApi.list(privileged && therapistFilter ? { therapistId: therapistFilter } : {}),
    retry: 1,
    enabled: online,
  });
  // Offline (or the server is unreachable): show the copies held on this device.
  const useLocalCopies = !online || list.isError;

  const therapists = useQuery({
    queryKey: ['assessment-therapists'],
    queryFn: staffApi.therapists,
    enabled: privileged,
  });

  // Push anything a closed tab left behind, then refresh.
  useEffect(() => {
    let cancelled = false;
    const refreshLocal = () => listForms(user.userId).then((r) => { if (!cancelled) setLocalRecords(r); }).catch(() => {});
    const syncThenRefresh = () => syncPendingForms(user.userId).then(({ synced }) => {
      refreshLocal();
      if (synced) queryClient.invalidateQueries({ queryKey: ['assessments'] });
    });
    refreshLocal();
    syncThenRefresh();
    window.addEventListener('online', syncThenRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('online', syncThenRefresh);
    };
  }, [user.userId, queryClient]);

  const localByUuid = useMemo(() => Object.fromEntries(localRecords.map((r) => [r.uuid, r])), [localRecords]);

  const forms = useMemo(() => {
    const server = useLocalCopies ? [] : (list.data?.forms || []);
    const known = new Set(server.map((f) => f.id));
    const localOnly = localRecords
      .filter((r) => !r.server && !known.has(r.uuid))
      .filter((r) => !therapistFilter || String(r.therapistId) === String(therapistFilter))
      .map(localSummary);
    // Offline: fall back to the last copy of each form held on this device.
    const offlineCopies = useLocalCopies
      ? localRecords.filter((r) => r.server && !known.has(r.uuid)).map((r) => r.server)
      : [];
    return [...localOnly, ...server, ...offlineCopies];
  }, [list.data, useLocalCopies, localRecords, therapistFilter]);

  const counts = useMemo(() => {
    const c = {};
    forms.forEach((f) => { c[f.status] = (c[f.status] || 0) + 1; });
    return c;
  }, [forms]);

  const current = TABS.find((t) => t.id === tab);
  const matchesTab = (t, f) => (t.statuses.includes(f.status) || (t.id === 'open' && f.status === 'local'))
    && (!t.survey || f.survey?.status === t.survey);
  const visible = forms.filter((f) => matchesTab(current, f));

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 md:text-2xl">Assessments</h1>
          <p className="text-sm text-slate-500">Initial Assessments and 12 Week Reviews</p>
        </div>
        <button type="button" onClick={() => setStarting(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700">
          <Plus className="h-4 w-4" /> Start assessment
        </button>
      </div>

      {privileged && online && (
        <label className="mb-4 block max-w-xs text-sm font-medium text-slate-700">
          Therapist
          <select value={therapistFilter} onChange={(e) => setTherapistFilter(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">All therapists</option>
            {(therapists.data?.therapists || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      )}

      {online && vault.offlineEnabled && !vault.hasPin && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-100 p-3 text-sm text-slate-700">
          <span>Offline access is available for your account but not set up on this device.</span>
          <button type="button" onClick={vault.enableOffline} className="font-medium text-blue-700 hover:underline">
            Enable offline access on this device
          </button>
        </div>
      )}

      {vault.offlineEnabled && vault.hasPin && <InstallHint />}

      {useLocalCopies && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
          <CloudOff className="h-4 w-4 shrink-0" />
          Can't reach the server. Showing assessments saved on this device.
        </div>
      )}

      <div className="-mx-4 mb-4 overflow-x-auto px-4 md:mx-0 md:px-0">
        <div className="flex gap-1 border-b border-slate-200" role="tablist">
          {TABS.map((t) => {
            const n = forms.filter((f) => matchesTab(t, f)).length;
            return (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
                className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm ${tab === t.id
                  ? 'border-blue-600 font-medium text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
                {t.label}
                {n > 0 && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 text-xs text-slate-600">{n}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {online && list.isLoading && !localRecords.length ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        </div>
      ) : visible.length ? (
        <ul className="space-y-3">
          {visible.map((f) => (
            <FormRow key={f.id} form={f} unsynced={pendingCount(localByUuid[f.id])} showTherapist={privileged} />
          ))}
        </ul>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          {tab === 'open' ? 'No open assessments. Start one to begin.' : `Nothing ${STATUS_META[tab]?.label.toLowerCase() || ''} here.`}
        </div>
      )}

      {starting && (
        <StartAssessmentDialog user={user} online={online} existingForms={forms} onClose={() => setStarting(false)} />
      )}
    </div>
  );
}
